import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ArrowLeft, MessageCircle, Phone, Store } from "lucide-react";
import { api, ApiError } from "../../lib/api";

/**
 * Anfrage von Hand erfassen - für alles, was NICHT über das Widget kommt:
 * Anrufe, WhatsApp-Nachrichten, Kunden an der Ladentheke.
 *
 * Bewusst schlank gehalten: die Bürokraft tippt während des Telefonats mit.
 * E-Mail ist freiwillig (wer anruft, hat selten eine parat) - ohne E-Mail
 * gibt es keine Status-Mails, aber der Tracking-Link lässt sich danach per
 * WhatsApp teilen.
 */

interface Service {
  id: string;
  label: string;
  subServices?: string[];
}

const QUELLEN = [
  { id: "telefon" as const, label: "Telefon", Icon: Phone },
  { id: "whatsapp" as const, label: "WhatsApp", Icon: MessageCircle },
  { id: "persoenlich" as const, label: "Persönlich", Icon: Store },
];

export function NeueAnfrage() {
  const navigate = useNavigate();
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api<{ services: Service[] }>("/admin/settings"),
  });
  const services = settings?.services ?? [];

  const [quelle, setQuelle] = useState<"telefon" | "whatsapp" | "persoenlich">("telefon");
  const [service, setService] = useState("");
  const [subService, setSubService] = useState("");
  const [urgency, setUrgency] = useState<"notfall" | "bald" | "planen">("planen");
  const [kundeTyp, setKundeTyp] = useState<"stammkunde" | "neukunde" | "">("");
  const [vorname, setVorname] = useState("");
  const [nachname, setNachname] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [plz, setPlz] = useState("");
  const [strasse, setStrasse] = useState("");
  const [beschreibung, setBeschreibung] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const gewaehlt = services.find((s) => s.id === service);

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    setLaeuft(true);
    try {
      const r = await api<{ id: string }>("/admin/anfragen", {
        method: "POST",
        body: {
          quelle,
          serviceCategory: service || "sonstiges",
          subService: subService || null,
          urgency,
          kundeTyp: kundeTyp || null,
          customerVorname: vorname,
          customerNachname: nachname,
          customerPhone: phone,
          customerEmail: email || null,
          customerPlz: plz || null,
          customerStrasse: strasse || null,
          description: beschreibung || null,
        },
      });
      navigate(`/app/anfragen/${r.id}`);
    } catch (err) {
      setFehler(err instanceof ApiError ? err.message : "Anfrage konnte nicht angelegt werden.");
      setLaeuft(false);
    }
  }

  return (
    <form onSubmit={absenden} className="mx-auto max-w-2xl space-y-5">
      <Link
        to="/app/dashboard"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> Zurück
      </Link>
      <div>
        <h1 className="page-title">Anfrage erfassen</h1>
        <p className="mt-1 text-sm text-slate-500">
          Für Anfragen per Telefon, WhatsApp oder an der Ladentheke — danach
          läuft alles wie bei einer Widget-Anfrage.
        </p>
      </div>

      <section className="card space-y-4">
        <div>
          <span className="mb-2 block text-sm font-medium text-slate-700">Wie kam die Anfrage rein?</span>
          <div className="flex flex-wrap gap-2">
            {QUELLEN.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setQuelle(id)}
                className={
                  "inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition " +
                  (quelle === id
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-400")
                }
              >
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs text-slate-500">Leistungsbereich</span>
            <select className="input" value={service}
              onChange={(e) => { setService(e.target.value); setSubService(""); }}>
              <option value="">– bitte wählen –</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-slate-500">Konkrete Leistung (optional)</span>
            <select className="input" value={subService}
              onChange={(e) => setSubService(e.target.value)}
              disabled={!gewaehlt?.subServices?.length}>
              <option value="">–</option>
              {(gewaehlt?.subServices ?? []).map((x) => (
                <option key={x} value={x}>{x}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-slate-500">Dringlichkeit</span>
            <select className="input" value={urgency}
              onChange={(e) => setUrgency(e.target.value as typeof urgency)}>
              <option value="planen">Flexibel / planen</option>
              <option value="bald">In den nächsten Tagen</option>
              <option value="notfall">Notfall</option>
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-slate-500">Kunde</span>
            <select className="input" value={kundeTyp}
              onChange={(e) => setKundeTyp(e.target.value as typeof kundeTyp)}>
              <option value="">– unbekannt –</option>
              <option value="stammkunde">Stammkunde</option>
              <option value="neukunde">Neukunde</option>
            </select>
          </label>
        </div>

        <label className="block">
          <span className="mb-1 block text-xs text-slate-500">Was ist zu tun?</span>
          <textarea className="input h-24" value={beschreibung}
            placeholder="Kurz notieren, was der Kunde geschildert hat …"
            onChange={(e) => setBeschreibung(e.target.value)} />
        </label>
      </section>

      <section className="card space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs text-slate-500">Vorname *</span>
            <input className="input" value={vorname} required
              onChange={(e) => setVorname(e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-slate-500">Nachname *</span>
            <input className="input" value={nachname} required
              onChange={(e) => setNachname(e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-slate-500">Telefon *</span>
            <input className="input" value={phone} required
              onChange={(e) => setPhone(e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-slate-500">PLZ</span>
            <input className="input" value={plz}
              onChange={(e) => setPlz(e.target.value)} />
          </label>
        </div>
        {/* Anschrift: für die Besichtigung und später für die Rechnung. */}
        <label className="block">
          <span className="mb-1 block text-xs text-slate-500">Straße und Hausnummer</span>
          <input className="input" value={strasse}
            onChange={(e) => setStrasse(e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-slate-500">E-Mail (freiwillig)</span>
          <input className="input" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} />
          <span className="mt-1 block text-xs text-slate-400">
            Mit E-Mail bekommt der Kunde Bestätigung und Status-Updates
            automatisch. Ohne E-Mail können Sie den Verfolgungs-Link danach
            per WhatsApp teilen.
          </span>
        </label>
      </section>

      {fehler && (
        <p className="inline-flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{fehler}</span>
        </p>
      )}

      <div className="flex justify-end">
        <button type="submit" className="btn-primary" disabled={laeuft}>
          {laeuft ? "Wird angelegt …" : "Anfrage anlegen"}
        </button>
      </div>
    </form>
  );
}
